import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  Upload,
  FileText,
  Play,
  RotateCw,
  CheckCircle,
  XCircle,
  Clock,
  Server,
  Mail,
  Loader2,
  AlertCircle,
  Terminal,
  Download,
  Trash2,
  RefreshCw
} from 'lucide-react'
import { 
  uploadAccountsFile, 
  inputAccountsManual, 
  configureMigration, 
  startMigration, 
  getProgress,
  getLogs,
  clearLogs
} from '@/services/api'

export default function Migrations() {
  const [activeTab, setActiveTab] = useState('upload')
  const [jobId, setJobId] = useState(null)
  const [jobName, setJobName] = useState('')
  const [file, setFile] = useState(null)
  const [manualInput, setManualInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(null)
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const fileInputRef = useRef(null)
  const intervalRef = useRef(null)
  const logsIntervalRef = useRef(null)

  const [config, setConfig] = useState({
    sourceHost: '',
    destinationHost: '',
    protocol: 'imaps',
    port: 993,
    accessLevel: 'SHELL_ACCESS',
    maxRetries: 3,
    timeout: 60,
    errorsMax: 50,
  })

  // Poll progress when migration is running
  useEffect(() => {
    if (jobId && (progress?.status === 'RUNNING' || !progress)) {
      intervalRef.current = setInterval(fetchProgress, 2000)
      return () => clearInterval(intervalRef.current)
    }
  }, [jobId, progress?.status])

  // Poll logs when on debug tab
  useEffect(() => {
    if (activeTab === 'debug' && jobId) {
      fetchLogs()
      logsIntervalRef.current = setInterval(fetchLogs, 3000)
      return () => clearInterval(logsIntervalRef.current)
    }
  }, [activeTab, jobId])

  const fetchProgress = async () => {
    if (!jobId) return
    try {
      const data = await getProgress(jobId)
      setProgress(data)
    } catch (err) {
      console.error('Error fetching progress:', err)
    }
  }

  const fetchLogs = async () => {
    if (!jobId) return
    setLogsLoading(true)
    try {
      const data = await getLogs(jobId)
      setLogs(data.logs || [])
    } catch (err) {
      console.error('Error fetching logs:', err)
    }
    setLogsLoading(false)
  }

  const handleClearLogs = async () => {
    try {
      await clearLogs(jobId)
      setLogs([])
    } catch (err) {
      console.error('Error clearing logs:', err)
    }
  }

  const handleFileUpload = async () => {
    if (!file) return
    setLoading(true)
    try {
      const response = await uploadAccountsFile(file, jobName)
      if (response.success) {
        setJobId(response.jobId)
        setActiveTab('configure')
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  const handleManualInput = async () => {
    if (!manualInput.trim()) return
    setLoading(true)
    try {
      const response = await inputAccountsManual(manualInput, jobName)
      if (response.success) {
        setJobId(response.jobId)
        setActiveTab('configure')
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  const handleConfigure = async () => {
    if (!config.sourceHost || !config.destinationHost) return
    setLoading(true)
    try {
      const response = await configureMigration(jobId, config)
      if (response.success) {
        setActiveTab('migrate')
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  const handleStartMigration = async () => {
    setLoading(true)
    try {
      await startMigration(jobId, config)
      fetchProgress()
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'RUNNING':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
      case 'PENDING':
        return <Clock className="w-4 h-4 text-gray-400" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const downloadLogs = () => {
    const logContent = logs.map(l => `[${l.timestamp}] [${l.level}] ${l.message}`).join('\n')
    const blob = new Blob([logContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `migration-${jobId}-logs.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-7">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Email Migrations</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Migrate email accounts from cPanel to Plesk servers
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="upload" disabled={!!jobId}>1. Upload</TabsTrigger>
          <TabsTrigger value="configure" disabled={!jobId || !!progress}>2. Configure</TabsTrigger>
          <TabsTrigger value="migrate" disabled={!jobId}>3. Migrate</TabsTrigger>
          <TabsTrigger value="debug" disabled={!jobId}>4. Debug</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Email Accounts</CardTitle>
              <CardDescription>
                Import accounts via CSV/TXT file or manual entry
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Job Name (Optional)</Label>
                <Input
                  placeholder="e.g., Company Migration Q1 2024"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {/* File Upload */}
                <Card className="border-dashed">
                  <CardContent className="pt-6">
                    <div
                      className="flex flex-col items-center justify-center py-8 cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        className="hidden"
                        onChange={(e) => setFile(e.target.files[0])}
                      />
                      <Upload className="w-10 h-10 text-muted-foreground mb-4" />
                      <p className="text-sm font-medium">
                        {file ? file.name : 'Click to upload file'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        CSV or TXT format
                      </p>
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      onClick={handleFileUpload}
                      disabled={!file || loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Upload & Continue
                    </Button>
                  </CardContent>
                </Card>

                {/* Manual Input */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm font-medium">Manual Entry</span>
                      </div>
                      <textarea
                        className="flex-1 min-h-[120px] p-3 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder={`user@example.com:your-password\nadmin@domain.com:your-password`}
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value)}
                      />
                      <Button 
                        className="w-full mt-4" 
                        variant="secondary"
                        onClick={handleManualInput}
                        disabled={!manualInput.trim() || loading}
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Submit & Continue
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
                <p className="font-medium mb-1">Format Guide:</p>
                <p>• CSV: email,password columns</p>
                <p>• TXT: email:password (one per line)</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configure Tab */}
        <TabsContent value="configure" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Migration Configuration</CardTitle>
              <CardDescription>
                Configure source and destination server settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2">
                  <Label>Source Host (cPanel)</Label>
                  <div className="relative">
                    <Server className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="mail.source-domain.com"
                      value={config.sourceHost}
                      onChange={(e) => setConfig({...config, sourceHost: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Destination Host (Plesk)</Label>
                  <div className="relative">
                    <Server className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="192.168.1.100"
                      value={config.destinationHost}
                      onChange={(e) => setConfig({...config, destinationHost: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Protocol</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={config.protocol}
                    onChange={(e) => setConfig({...config, protocol: e.target.value})}
                  >
                    <option value="imaps">IMAP with SSL (993)</option>
                    <option value="imap">IMAP without SSL (143)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Access Level</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={config.accessLevel}
                    onChange={(e) => setConfig({...config, accessLevel: e.target.value})}
                  >
                    <option value="ROOT_ACCESS">Root Access</option>
                    <option value="SHELL_ACCESS">Shell Access</option>
                    <option value="IMAP_ONLY">IMAP Only</option>
                  </select>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>Max Retries</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={config.maxRetries}
                    onChange={(e) => setConfig({...config, maxRetries: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timeout (seconds)</Label>
                  <Input
                    type="number"
                    min="30"
                    max="300"
                    value={config.timeout}
                    onChange={(e) => setConfig({...config, timeout: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Error Tolerance</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={config.errorsMax}
                    onChange={(e) => setConfig({...config, errorsMax: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={handleConfigure}
                disabled={!config.sourceHost || !config.destinationHost || loading}
              >
                Save Configuration
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Migrate Tab */}
        <TabsContent value="migrate" className="space-y-4">
          {progress && (
            <Card>
              <CardHeader>
                <CardTitle>Migration Progress</CardTitle>
                <CardDescription>
                  {progress.jobName} • {progress.totalAccounts} accounts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Overview */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{progress.progressPercentage}% Complete</span>
                    <span>{progress.completedAccounts + progress.failedAccounts} / {progress.totalAccounts}</span>
                  </div>
                  <Progress value={progress.progressPercentage} className="h-2" />
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-green-50 dark:bg-green-950">
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {progress.completedAccounts}
                      </div>
                      <div className="text-xs text-muted-foreground">Successful</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 dark:bg-red-950">
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {progress.failedAccounts}
                      </div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 dark:bg-blue-950">
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {progress.runningAccounts}
                      </div>
                      <div className="text-xs text-muted-foreground">Running</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold">
                        {progress.totalEmailsMigrated.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">Emails</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Account List */}
                <div className="border rounded-md">
                  <div className="grid grid-cols-12 gap-2 p-3 text-xs font-medium text-muted-foreground bg-muted border-b">
                    <div className="col-span-4">Email</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Phase</div>
                    <div className="col-span-2">Emails</div>
                    <div className="col-span-2">Retries</div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {progress.accounts?.map((account) => (
                      <div 
                        key={account.email} 
                        className="grid grid-cols-12 gap-2 p-3 text-sm border-b last:border-0 items-center"
                      >
                        <div className="col-span-4 truncate flex items-center gap-2">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          {account.email}
                        </div>
                        <div className="col-span-2 flex items-center gap-1">
                          {getStatusIcon(account.status)}
                          <span className="text-xs">{account.status}</span>
                        </div>
                        <div className="col-span-2 text-xs text-muted-foreground">
                          {account.phase}
                        </div>
                        <div className="col-span-2 text-xs">
                          {account.emailsMigrated > 0 ? account.emailsMigrated.toLocaleString() : '-'}
                        </div>
                        <div className="col-span-2 text-xs">
                          {account.retryCount > 0 && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <RotateCw className="w-3 h-3" />
                              {account.retryCount}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Error Summary */}
                {progress.failedAccounts > 0 && (
                  <Card className="border-red-200 bg-red-50 dark:bg-red-950/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-red-700 dark:text-red-300">
                        <AlertCircle className="w-4 h-4" />
                        Errors Detected
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {progress.failedAccounts} account(s) failed to migrate. 
                        Check the <button 
                          onClick={() => setActiveTab('debug')} 
                          className="underline font-medium"
                        >Debug</button> tab for details.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                {progress.status === 'CREATED' && (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handleStartMigration}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Start Migration
                  </Button>
                )}

                {progress.status === 'COMPLETED' && (
                  <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 rounded-md">
                    <CheckCircle className="w-5 h-5" />
                    <span>Migration completed successfully!</span>
                  </div>
                )}

                {progress.status === 'FAILED' && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <span>Migration failed. Check Debug tab for details.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Debug Tab */}
        <TabsContent value="debug" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Terminal className="w-5 h-5" />
                    Process Logs
                  </CardTitle>
                  <CardDescription>
                    Real-time logs and error messages from the migration process
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchLogs}
                    disabled={logsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${logsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={downloadLogs}
                    disabled={logs.length === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleClearLogs}
                    disabled={logs.length === 0}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Terminal className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No logs available yet.</p>
                  <p className="text-sm">Start a migration to see process logs.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Log Filters */}
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                      INFO
                    </span>
                    <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded">
                      WARN
                    </span>
                    <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded">
                      ERROR
                    </span>
                  </div>

                  {/* Log Container */}
                  <div 
                    className="bg-black text-green-400 font-mono text-sm p-4 rounded-md overflow-auto max-h-[500px]"
                    style={{ 
                      backgroundColor: '#0d1117',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                    }}
                  >
                    {logs.map((log, index) => (
                      <div 
                        key={index} 
                        className={`mb-1 ${
                          log.level === 'ERROR' ? 'text-red-400' : 
                          log.level === 'WARN' ? 'text-yellow-400' : 
                          'text-green-400'
                        }`}
                      >
                        <span className="text-gray-500">[{log.timestamp}]</span>
                        <span className={`ml-2 px-1.5 rounded text-xs ${
                          log.level === 'ERROR' ? 'bg-red-900/50 text-red-300' :
                          log.level === 'WARN' ? 'bg-yellow-900/50 text-yellow-300' :
                          'bg-green-900/50 text-green-300'
                        }`}>
                          {log.level}
                        </span>
                        <span className="ml-2">{log.message}</span>
                        {log.account && (
                          <span className="ml-2 text-blue-400">({log.account})</span>
                        )}
                      </div>
                    ))}
                    <div ref={(el) => el?.scrollIntoView({ behavior: 'smooth' })} />
                  </div>

                  {/* Error Summary */}
                  {logs.filter(l => l.level === 'ERROR').length > 0 && (
                    <Card className="mt-4 border-red-200 bg-red-50 dark:bg-red-950/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-red-700 dark:text-red-300">
                          Error Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {logs
                            .filter(l => l.level === 'ERROR')
                            .slice(-5)
                            .map((error, idx) => (
                              <li 
                                key={idx} 
                                className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2"
                              >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{error.message}</span>
                              </li>
                            ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
